import { EditIcon, SaveButton } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { useTaxCouponList } from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  CouponOriginEntity,
  TaxCouponStatus,
  TaxCouponStatusColor,
  TaxCouponStatusLabel,
} from '@/core/domain/coupon/coupons/coupons.types';
import { InvoiceSituationColor } from '@/core/domain/invoice/invoice/Invoice.types';
import {
  InvoiceType,
  InvoiceSituation,
} from '@/core/domain/invoice/invoices/invoices.enums';
import {
  InvoiceData,
  InvoiceOriginCategory,
} from '@/core/domain/invoice/invoices/invoices.types';
import {
  InvoiceEnumValuesTypes,
  InvoiceValuesItem,
  InvoiceValuesItemType,
  InvoiceValuesTypes,
  WorkOrderComplement,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useDisclosure } from '@/hooks';
import {
  WorkorderInvoiceValuesType,
  workorderCouponOriginTypes,
} from '@/pages/MovementsPage/WorkOrdersPage/WorkOrderDetailsPage/InvoicePage';
import { getWorkorderComplementNumber } from '@/utils/functions/getWorkorderComplementNumber';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Popover,
  Space,
  Table,
  Tag,
} from 'antd';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import formatterPrice from '@/utils/number/formatterPrice';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { MathRound } from '@/utils/functions/MathRound';

interface Props {
  title: string;
  invoiceValues: Record<string, InvoiceValuesItem>;
  onSubmit(data: InvoiceValuesItem[]): void;
  loading: boolean;
  invoiceItems: InvoiceData[];
  complements?: (WorkOrderComplement & {
    checked: boolean;
  })[];
  modelType?: number;
  companyId?: number;
  invoiceValuesType: WorkorderInvoiceValuesType;
}

const WorkOrderInvoiceValues: React.FC<Props> = ({
  title,
  invoiceValues,
  invoiceItems,
  onSubmit,
  loading,
  complements,
  modelType,
  companyId,
  invoiceValuesType,
}) => {
  const [keys, setKeys] = useState<number[]>([]);
  const hasChecked = !!keys.length;

  const hasCFE = modelType === 59;

  const intl = useIntl();
  const history = useHistory();

  const [modalOpened, modalAction] = useDisclosure(false);
  const [couponModalOpened, couponModalAction] = useDisclosure(false);

  const {
    workorder: { data: workorder },
  } = useWorkorderDetailContext();

  const { data: taxCouponsData } = useTaxCouponList({
    companyId,
    pageSize: 50,
    entityId: complements?.map((item) => String(item.id)),
    entityType: workorderCouponOriginTypes[invoiceValuesType],
    entityCode: complements?.map((item) =>
      getWorkorderComplementNumber(workorder?.number, item.number),
    ),
    entity: CouponOriginEntity.WORK_ORDER,
  });

  const taxCoupons = flatInfiniteData(taxCouponsData);
  const hasCoupons = !!taxCoupons.length;

  const data = Object.values(invoiceValues).filter((item) => item);

  const getInvoiceCategory = useCallback(
    (invoiceCategory?: InvoiceOriginCategory) => {
      switch (invoiceCategory) {
        case InvoiceOriginCategory.INSURANCE:
          return invoiceValuesType === WorkorderInvoiceValuesType.insurer;

        case InvoiceOriginCategory.PRIVATE:
          return (
            invoiceValuesType === WorkorderInvoiceValuesType.particular ||
            invoiceValuesType === WorkorderInvoiceValuesType.franchise
          );

        case InvoiceOriginCategory.SUPPLIER:
          return invoiceValuesType === WorkorderInvoiceValuesType.porto;
      }
    },
    [invoiceValuesType],
  );

  const invoicesData = invoiceItems.filter((invoiceItem) =>
    data
      .flatMap((dataItem) => dataItem.items)
      .some((item) => {
        if (item.workOrderComplementId) {
          return invoiceItem.originReference.some(
            (reference) =>
              reference.entityId === item.workOrderComplementId &&
              getInvoiceCategory(reference.invoiceCategory),
          );
        }

        return invoiceItem.originReference.some((reference) =>
          complements?.some(
            (complement) =>
              complement.id === reference.entityId &&
              getInvoiceCategory(reference.invoiceCategory),
          ),
        );
      }),
  );

  function handleSubmit() {
    onSubmit(keys.map((key) => data[key]));
    setKeys([]);
  }

  function getTotalValue(row: InvoiceValuesItem) {
    const calc = row.items.reduce(
      (acc, item) =>
        acc + MathRound(item.unitPrice * item.quantity - item.valueDiscount, 2),
      0,
    );

    return calc;
  }

  const hasInvoiceGenerated = useCallback(() => {
    return complements?.some((complement) =>
      data.some(
        (invoiceValues, index) =>
          keys.includes(index) &&
          invoiceValues.items.some(
            (item) => item.workOrderComplementId === complement.id,
          ) &&
          invoiceItems.some((invoiceItem) => {
            if (
              invoiceItem.originReference.some(
                (reference) =>
                  reference.entityId === complement.id &&
                  getInvoiceCategory(reference.invoiceCategory),
              ) &&
              invoiceItem.situation !== InvoiceSituation.DENIED
            ) {
              return (
                ((invoiceItem.invoiceType === InvoiceType.NFS &&
                  invoiceValues.type === InvoiceValuesItemType.Service) ||
                  (invoiceItem.invoiceType !== InvoiceType.NFS &&
                    invoiceValues.type === InvoiceValuesItemType.Part)) &&
                invoiceItem.situation !== InvoiceSituation.CANCELED
              );
            }
            return false;
          }),
      ),
    );
  }, [keys, data, complements]);

  function hasInvoiceValuesGenerated(invoiceValues: InvoiceValuesItem) {
    return complements?.some(
      (complement) =>
        invoiceValues.items.some(
          (item) =>
            item.workOrderComplementId === 0 ||
            item.workOrderComplementId === complement.id,
        ) &&
        invoiceItems.some((invoiceItem) => {
          if (
            invoiceItem.originReference.some(
              (reference) =>
                reference.entityId === complement.id &&
                getInvoiceCategory(reference.invoiceCategory),
            ) &&
            invoiceItem.situation !== InvoiceSituation.DENIED
          ) {
            return (
              ((invoiceItem.invoiceType === InvoiceType.NFS &&
                invoiceValues.type === InvoiceValuesItemType.Service) ||
                (invoiceItem.invoiceType !== InvoiceType.NFS &&
                  invoiceValues.type === InvoiceValuesItemType.Part)) &&
              invoiceItem.situation !== InvoiceSituation.CANCELED
            );
          }
          return false;
        }),
    );
  }

  const hasCouponValuesGenerated = useCallback(
    (dataItem: InvoiceValuesItem) => {
      return complements?.some(
        (complement) =>
          dataItem.items.some(
            (item) => item.workOrderComplementId === complement.id,
          ) &&
          taxCoupons.some((coupon) => {
            return (
              coupon?.origins?.some(
                (reference) =>
                  reference.entityId === String(complement.id) &&
                  reference.entityType ===
                    workorderCouponOriginTypes[invoiceValuesType],
              ) &&
              coupon.couponItems.every((item) =>
                dataItem.items.some(
                  (dataItem) =>
                    dataItem.id === item.itemId ||
                    dataItem.description === item.description,
                ),
              )
            );
          }),
      );
    },
    [taxCoupons, complements],
  );

  return (
    <>
      <Col span={24}>
        <Card
          title={title}
          extra={
            <Space>
              {!!invoicesData.length && (
                <Button type="primary" onClick={modalAction.open}>
                  Visualizar Notas
                </Button>
              )}

              {hasCoupons && (
                <Button type="primary" onClick={couponModalAction.open}>
                  Visualizar Cupons
                </Button>
              )}

              {!hasCFE && (
                <SaveButton
                  block
                  disabled={!hasChecked || hasInvoiceGenerated()}
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Gerar Nota
                </SaveButton>
              )}

              {hasCFE && (
                <SaveButton
                  block
                  disabled={!hasChecked}
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Gerar Cupom
                </SaveButton>
              )}
            </Space>
          }
          style={{ borderRadius: 10, position: 'relative', overflow: 'hidden' }}
        >
          {loading && (
            <ModalLoadingOverlay
              messages={['Gerando nota...']}
              style={{ position: 'absolute', width: '100%', height: '100%' }}
            />
          )}

          {hasCFE && (
            <Alert
              type="warning"
              description="Cupom de serviço está em processo de análise interna."
              style={{ marginBottom: 16 }}
            />
          )}

          <Table
            dataSource={data}
            rowKey={(_, index) => Number(index)}
            columns={[
              {
                dataIndex: ['client', 'name'],
                title: 'Destinatário',
                ellipsis: true,
                width: 200,
              },
              {
                dataIndex: 'type',
                title: 'Tipo',
                width: 100,
                render: (type: InvoiceValuesItemType) =>
                  InvoiceValuesTypes[type],
              },
              {
                dataIndex: 'model',
                title: 'Modelo',
                align: 'right',
                width: 100,
                render: (value) => value || '-',
              },
              {
                dataIndex: 'valueDiscount',
                title: 'Desconto(R$)',
                align: 'right',
                render: (_, row) =>
                  formatterPrice(
                    row.items.reduce(
                      (acc, item) => acc + item.valueDiscount,
                      0,
                    ),
                  ),
                width: 100,
              },
              {
                dataIndex: 'unitPrice',
                title: 'Valor(R$)',
                align: 'right',
                render: (_, row) =>
                  formatterPrice(
                    row.items.reduce(
                      (acc, item) => acc + item.unitPrice * item.quantity,
                      0,
                    ),
                  ),
                width: 100,
              },
              {
                title: 'Total(R$)',
                align: 'right',
                render: (_, row) => formatterPrice(getTotalValue(row)),
                width: 100,
              },
            ]}
            rowSelection={{
              selectedRowKeys: keys,
              onChange: (rowKeys: unknown) => setKeys(rowKeys as number[]),
              getCheckboxProps: (row) => ({
                disabled:
                  (row.type === InvoiceValuesItemType.Service && hasCFE) ||
                  getTotalValue(row) <= 0 ||
                  (hasCFE
                    ? hasCouponValuesGenerated(row)
                    : hasInvoiceValuesGenerated(row)),
              }),
            }}
            scroll={{ x: true }}
            pagination={false}
          />
        </Card>
      </Col>

      <Modal
        open={modalOpened}
        onCancel={modalAction.close}
        title="Notas fiscais geradas"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Fechar"
        width={700}
        maskClosable={false}
      >
        <Table
          dataSource={invoicesData}
          columns={[
            {
              title: 'Destinatário',
              width: 200,
              ellipsis: true,
              render: (_, row) =>
                (!!row.customerName && row.customerName) ||
                (!!row.insurerName && row.insurerName) ||
                (!!row.supplierName && row.supplierName) ||
                '-',
            },
            {
              title: 'OS/Complemento',
              width: 150,
              ellipsis: true,
              render: (_, row) => (
                <Space>
                  <span>
                    {!!row.originReference.length &&
                      row.originReference[0].entityCode}
                  </span>
                  {row.originReference.length > 1 && (
                    <Popover
                      content={
                        <>
                          {row.originReference.slice(1).map((reference) => (
                            <div>{reference.entityCode}</div>
                          ))}
                        </>
                      }
                    >
                      <Tag>{`+${row.originReference.length - 1}`}</Tag>
                    </Popover>
                  )}
                </Space>
              ),
            },
            {
              dataIndex: 'invoiceType',
              title: 'Tipo',
              width: 100,
              ellipsis: true,
              render: (type: InvoiceType) =>
                InvoiceEnumValuesTypes[type] || '-',
            },
            {
              dataIndex: 'situation',
              title: 'Situação',
              width: 100,
              render: (value: InvoiceSituation) =>
                value ? (
                  <StatusTag
                    color={InvoiceSituationColor[value]}
                    label={intl.formatMessage({
                      id: `invoice.situation.${value}`,
                    })}
                  />
                ) : (
                  '-'
                ),
            },
            {
              title: 'Ação',
              width: 50,
              align: 'center',
              render: (_, row) => (
                <EditIcon
                  iconProps={{
                    onClick: () =>
                      history.push(
                        `/app/fiscal/invoice/${row.invoiceType}/${row.id}/datas`,
                      ),
                  }}
                />
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>

      <Modal
        open={couponModalOpened}
        onCancel={couponModalAction.close}
        title="Cupons fiscais gerados"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Fechar"
        width={700}
        maskClosable={false}
      >
        <Table
          dataSource={taxCoupons}
          columns={[
            {
              title: 'Cliente',
              dataIndex: ['recipient', 'clientName'],
              width: 200,
              ellipsis: true,
            },
            {
              title: 'OS/Complemento',
              width: 150,
              ellipsis: true,
              render: (_, row) => (
                <Space>
                  <span>
                    {!!row?.origins?.length && row?.origins
                      ? row.origins[0].entityCode
                      : null}
                  </span>

                  {(row?.origins?.length || 0) > 1 && (
                    <Popover
                      content={
                        <>
                          {row?.origins?.slice(1).map((reference) => (
                            <div>{reference.entityCode}</div>
                          ))}
                        </>
                      }
                    >
                      <Tag>{`+${(row?.origins?.length || 0) - 1}`}</Tag>
                    </Popover>
                  )}
                </Space>
              ),
            },
            {
              dataIndex: 'status',
              title: 'Situação',
              width: 100,
              render: (status: TaxCouponStatus) =>
                status ? (
                  <StatusTag
                    color={TaxCouponStatusColor[status]}
                    label={TaxCouponStatusLabel[status]}
                  />
                ) : (
                  '-'
                ),
            },
            {
              title: 'Ação',
              width: 50,
              align: 'center',
              render: (_, row) => (
                <EditIcon
                  iconProps={{
                    onClick: () =>
                      window.open(
                        `/app/fiscal/cfe/${row.id}/${companyId}/general`,
                        '_blank',
                      ),
                  }}
                />
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>
    </>
  );
};

export default WorkOrderInvoiceValues;
